import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/context/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { BookOpen, Loader2 } from "lucide-react";
import { BasicInformationCard } from "./components/BasicInformationCard";
import { ScheduleDetailsCard } from "./components/ScheduleDetailsCard";
import { StateRequirementsCard } from "./components/StateRequirementsCard";
import { formatDateForStorage } from "@/lib/utils/date-utils";
import { createLogger } from "@/lib/utils/logger";

const logger = createLogger('api', 'CreateCourse');

const eventTypeOptions = [
  { value: "live", label: "In-Person" },
  { value: "self_paced", label: "Self-Paced" },
  { value: "virtual", label: "Virtual Event" },
];

const targetAudienceOptions = [
  { value: "all", label: "All" },
  { value: "teachers", label: "Teachers" },
  { value: "administrators", label: "Administrators" },
  { value: "support_staff", label: "Support Staff" }
];

const CreateCourse = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: "",
    description: "",
    hours: 0,
    event_type: "live" as "live" | "self_paced" | "virtual",
    status: "published" as "draft" | "published" | "archived" | "cancelled",
    max_participants: 0,
    venue_details: "",
    start_date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    presenter: "",
    presenter_bio: "",
    location: "NWSISD Office Suite",
    target_audience: "all" as "teachers" | "administrators" | "support_staff" | "all",
    subject_area: "",
    category: "Professional Development",
    event_banner_url: "",
    bosa_verified: false,
    state_requirements: {
      behavioralIntervention: false,
      behavioralInterventionHours: 0,
      readingPreparation: false,
      readingPreparationHours: 0,
      keyWarningSigns: false,
      keyWarningSignsHours: 0,
      suicidePrevention: false,
      suicidePreventionHours: 0,
      englishLearners: false,
      englishLearnersHours: 0,
      americanIndianHistory: false,
      americanIndianHistoryHours: 0,
      culturalCompetency: {
        enabled: false,
        enabledHours: 0,
        racial: false,
        religion: false,
        systemicRacism: false,
        genderIdentity: false,
        sexualOrientation: false,
        languageDiversity: false,
        disabilities: false
      }
    }
  });

  const updateTotalHours = (totalHours: number) => {
    setNewCourse(prev => ({
      ...prev,
      hours: totalHours
    }));
  };

  const validateForm = () => {
    const errors = [];

    if (!newCourse.title) errors.push("Title is required");
    if (!newCourse.hours || newCourse.hours <= 0) errors.push("At least one State Requirement with hours must be selected");
    if (!newCourse.presenter) errors.push("Presenter is required");
    if (!newCourse.event_type) errors.push("Event type is required");
    if (!newCourse.target_audience) errors.push("Target audience is required");

    if (newCourse.event_type === 'live') {
      if (!newCourse.start_date) errors.push("Start date is required");
      if (!newCourse.end_date) errors.push("End date is required");
      if (newCourse.start_date > newCourse.end_date) {
        errors.push("End date must be after start date");
      }
      if (!newCourse.start_time) errors.push("Start time is required");
      if (!newCourse.end_time) errors.push("End time is required");
      if (newCourse.start_date === newCourse.end_date && 
          newCourse.start_time >= newCourse.end_time) {
        errors.push("End time must be after start time");
      }
    }

    return errors;
  };

  const handleCreateCourse = async () => {
    const errors = validateForm();
    
    if (errors.length > 0) {
      toast({
        title: "Validation Error",
        description: errors.join(". "),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Format dates properly before sending to database
      // Always store as T12:00:00Z (noon UTC) to avoid timezone issues
      const startDate = formatDateForStorage(newCourse.start_date ? `${newCourse.start_date}T12:00:00Z` : undefined);
      const endDate = formatDateForStorage(newCourse.end_date ? `${newCourse.end_date}T12:00:00Z` : undefined);
      
      logger.info(`Formatting dates for database: start_date=${newCourse.start_date} → ${startDate}, end_date=${newCourse.end_date} → ${endDate}`);
      
      const courseData = {
        title: newCourse.title.trim(),
        description: newCourse.description?.trim(),
        hours: Number(newCourse.hours),
        event_type: newCourse.event_type,
        status: newCourse.status,
        max_participants: newCourse.max_participants ? Number(newCourse.max_participants) : null,
        venue_details: newCourse.venue_details?.trim(),
        start_date: newCourse.event_type === 'live' && startDate ? startDate : null,
        end_date: newCourse.event_type === 'live' && endDate ? endDate : null,
        target_audience: newCourse.target_audience,
        subject_area: newCourse.subject_area?.trim(),
        category: newCourse.category?.trim(),
        created_by: profile?.id,
        start_time: newCourse.start_time || null,
        end_time: newCourse.end_time || null,
        presenter: newCourse.presenter?.trim() || null,
        presenter_bio: newCourse.presenter_bio || null,
        location: newCourse.location?.trim() || "NWSISD Office Suite",
        event_banner_url: newCourse.event_banner_url || null,
        bosa_verified: newCourse.bosa_verified || false,
        state_requirements: newCourse.state_requirements
      };

      const { error, data } = await supabase
        .from('courses')
        .insert([courseData])
        .select()
        .single();

      if (error) throw error;

      logger.info(`Course created successfully: ${data.id}`);
      toast({
        title: "Success",
        description: "Course created successfully!",
      });

      navigate('/admin/courses');
    } catch (error: any) {
      console.error('Error creating course:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create course. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full py-1 px-1 space-y-4 max-w-7xl mx-auto">
      <div className="page-header">
        <div className="flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-[#004161]" />
          <h1>Create New Course</h1>
        </div>
        <p>Fill in the course details below. Fields marked with * are required.</p>
      </div>

      <div className="grid gap-4">
        <BasicInformationCard
          newCourse={newCourse}
          setNewCourse={setNewCourse}
          eventTypeOptions={eventTypeOptions}
          targetAudienceOptions={targetAudienceOptions}
        />

        <ScheduleDetailsCard
          newCourse={newCourse}
          setNewCourse={setNewCourse}
        />

        <StateRequirementsCard
          newCourse={newCourse}
          setNewCourse={setNewCourse}
          updateTotalHours={updateTotalHours}
        />

        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/courses')}
            className="font-section text-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateCourse}
            disabled={isSubmitting}
            className="font-section text-sm"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Course'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateCourse;
